import {Avatar, AvatarFallback} from "../../../basic/avatar.tsx";
import {Badge} from "../../../basic/badge";
import {LicenseDisplay} from "./LicenseDisplay.tsx";
import {MemberActions} from "./MemberActions.tsx";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {ColumnDef, DataTable} from "../../../shared/tables/DataTable.tsx";
import {VwpTeamMember} from "../../../../store/generated/api.ts";
import {getDisplayName, getInitials} from "../../../../utils/nameUtils.ts";
import {formatDate} from "../../../../utils/dateUtils.ts";

interface MemberTableProps {
    paginatedData: VwpTeamMember[];
    onRemoveMember: (member: VwpTeamMember) => void;
    onManageLicenses: (member: VwpTeamMember) => void;
}

export function MemberTable({
                                paginatedData,
                                onRemoveMember,
                                onManageLicenses
                            }: MemberTableProps) {
    const {t} = useTranslationWithKeys();

    const getLatestLogin = (member: VwpTeamMember): string | null => {
        const platformLogin = member.lastPlatformLogin ? new Date(member.lastPlatformLogin) : null;
        const licenseLogin = member.lastLicenseLogin ? new Date(member.lastLicenseLogin) : null;

        if (!platformLogin && !licenseLogin) return null;
        if (!platformLogin) return licenseLogin.toISOString();
        if (!licenseLogin) return platformLogin.toISOString();

        return platformLogin > licenseLogin ? platformLogin.toISOString() : licenseLogin.toISOString();
    };

    const isOnline = (loginDate: string | null): boolean => {
        if (!loginDate) return false;
        const login = new Date(loginDate);
        const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
        return login > fifteenMinutesAgo;
    };
    const columns: ColumnDef<VwpTeamMember>[] = [
        {
            key: "email",
            header: t('common.table.name'),
            cell: (member) => (
                <div className="flex items-center gap-3">
                    <Avatar>
                        <AvatarFallback>{getInitials(member)}</AvatarFallback>
                    </Avatar>
                    <div>
                        <p>{getDisplayName(member)}</p>
                        <p className="text-muted-foreground">{member.email}</p>
                    </div>
                </div>
            )
        },
        {
            key: "role",
            header: t('common.table.role'),
            cell: (member) => (
                <Badge variant={member.role === "ADMIN" ? "default" : "outline"}>
                    {t(`common.role.${member.role.toLowerCase()}`)}
                </Badge>
            )
        },
        {
            key: "licenses",
            header: t('common.table.licenses'),
            cell: (member) => <LicenseDisplay licenses={member.licenses}/>
        },
        {
            key: "joinedDate",
            header: t('common.time.activeSince'),
            cell: (member) => (
                <span className={member.joinedDate ? "" : "text-muted-foreground"}>
                    {member.joinedDate ? formatDate(member.joinedDate) : t('common.never')}
                </span>
            )
        },
        {
            key: "email",
            header: t('common.time.lastActive'),
            cell: (member) => {
                const latestLogin = getLatestLogin(member);
                const online = isOnline(latestLogin);

                return (
                    <div className="flex items-center gap-2">
                        {online && (
                            <span className="h-2 w-2 rounded-full bg-green-500"/>
                        )}
                        <span>
                            {latestLogin ? formatDate(latestLogin) : t('common.never')}
                        </span>
                    </div>
                );
            }
        },
        {
            key: "id",
            header: t('common.actions'),
            className: "w-[200px]",
            cell: (member) => (
                <MemberActions
                    member={member}
                    onRemoveMember={onRemoveMember}
                    onManageLicenses={onManageLicenses}
                />
            )
        }
    ];

    return (
        <DataTable
            data={paginatedData}
            columns={columns}
            withBorder={true}
        />
    );
}